import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SOLUTIONS } from "@/config/site";
import GlobeArt from "@/components/GlobeArt";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [0, 70]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || reduce) return undefined;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    if (v.readyState >= 2) tryPlay();
    else v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, [reduce]);

  return (
    <section ref={ref} className="relative bg-platinum" data-testid="hero-section">
      <div className="relative z-10 mx-auto max-w-5xl px-4 pt-12 text-center sm:px-6 sm:pt-16 lg:px-8 lg:pt-20">
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="font-display text-xs font-bold uppercase tracking-[0.28em] text-cobalt"
          data-testid="hero-eyebrow"
        >
          DJC Global
        </motion.p>

        <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight leading-[1.08] text-ink sm:text-5xl lg:text-6xl">
          <span className="block overflow-hidden">
            <motion.span
              className="block"
              initial={reduce ? false : { y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            >
              A world of opportunity.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block"
              initial={reduce ? false : { y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.24 }}
            >
              Built around <span className="text-cobalt">your business.</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.42 }}
          className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg"
          data-testid="hero-subcopy"
        >
          Connect with specialized solutions for business savings, employee benefits, healthcare
          operations, AI marketing, and what comes next.
        </motion.p>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.54 }}
          className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <Link
            to="/solutions"
            data-testid="hero-primary-cta"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-cobalt px-7 py-3.5 font-display text-sm font-semibold text-white shadow-sm transition-colors hover:bg-midnight sm:w-auto"
          >
            Explore Your Opportunities
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-secondary-cta"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-ink/20 bg-white px-7 py-3.5 font-display text-sm font-semibold text-ink transition-colors hover:border-cobalt hover:text-cobalt sm:w-auto"
          >
            Talk With DJC Global
          </Link>
        </motion.div>
      </div>

      <div className="relative mt-10 sm:mt-14" data-testid="hero-panorama">
        <motion.div style={{ y: imgY }} className="h-[320px] w-full sm:h-[420px] lg:h-[520px]">
          <video
            ref={videoRef}
            role="img"
            aria-label="Digital world map with glowing global connection lines"
            poster="/hero-video-poster.jpg"
            muted
            loop
            playsInline
            autoPlay={!reduce}
            preload="metadata"
            className="h-full w-full object-cover"
            data-testid="hero-panorama-video"
          >
            <source src="/hero-global.webm" type="video/webm" />
            <source src="/hero-global.mp4" type="video/mp4" />
          </video>
        </motion.div>
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-2/5 bg-gradient-to-b from-platinum via-platinum/55 to-transparent"
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-platinum to-transparent"
        />
        <GlobeArt
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[60%] w-full opacity-80"
          stroke="#5B9BE6"
          stroke2="#C6CED8"
        />
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8" data-testid="hero-service-index">
        <div className="flex items-center gap-4">
          <span className="font-display text-xs font-bold uppercase tracking-[0.25em] text-cobalt">
            The Atlas — Seven Destinations
          </span>
          <div className="h-px flex-1 bg-silver/60" />
        </div>
        <ul className="mt-6 grid grid-cols-1 gap-x-10 sm:grid-cols-2 lg:grid-cols-4">
          {SOLUTIONS.map((s) => (
            <li key={s.id}>
              <Link
                to={`/solutions/${s.slug}`}
                data-testid={`hero-service-link-${s.slug}`}
                className="group flex items-baseline gap-3 border-b border-silver/40 py-2.5 transition-colors hover:border-cobalt"
              >
                <span className="font-display text-xs font-bold text-cobalt">{s.num}</span>
                <span className="text-sm font-medium text-ink transition-colors group-hover:text-cobalt">
                  {s.name}
                </span>
                <ArrowUpRight className="ml-auto h-3.5 w-3.5 shrink-0 text-silver transition-colors group-hover:text-cobalt" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
